import { RefObject, useContext, useEffect, useState } from 'react';
import { ThemeContext } from '@shared/contexts/ThemeContext';
import {
  getContrastRatio,
  getEffectiveBackgroundColor,
  WCAG_AA_TEXT_CONTRAST_RATIO,
} from '@shared/utils/color';

/**
 * Determines whether an element needs a border to remain distinguishable from
 * its surroundings, based on the WCAG AA text contrast ratio between the
 * element's background color and the effective background of its parent.
 */
export function useNeedsContrastBorder(
  elementReference: RefObject<HTMLElement | null>,
  backgroundColor: string,
): boolean {
  const theme = useContext(ThemeContext);
  const [needsBorder, setNeedsBorder] = useState(false);

  useEffect(() => {
    const element = elementReference.current;
    if (!element) {
      return;
    }

    const parentBackgroundColor = getEffectiveBackgroundColor(
      element.parentElement,
    );
    const contrastRatio = getContrastRatio(
      backgroundColor,
      parentBackgroundColor,
    );

    setNeedsBorder(
      contrastRatio !== null && contrastRatio < WCAG_AA_TEXT_CONTRAST_RATIO,
    );
  }, [elementReference, backgroundColor, theme]);

  return needsBorder;
}
